<script setup>
import { ref, computed, onMounted } from "vue";

import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import SnsSelect from "@/components/ui/SnsSelect.vue";
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT } from "@/lib/constants/dateTimeFormats";
import { getSelectedDateFormatOptions, getSelectedTimeFormatOptions } from "@/lib/utils/format";
import { toaster } from "@/plugins/toaster";

defineProps({
  showExamples: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();
const { t } = useI18n();

const selectedDateFormat = ref({});
const selectedTimeFormat = ref({});
const dateFormats = ref([]);
const timeFormats = ref([]);

const getSelectedDateFormat = computed(() => store.getters.getSelectedDateFormat);
const getSelectedTimeFormat = computed(() => store.getters.getSelectedTimeFormat);

const canSave = computed(() => {
  return (
    selectedDateFormat.value.value !== getSelectedDateFormat.value ||
    selectedTimeFormat.value.value !== getSelectedTimeFormat.value
  );
});

const saveFormats = () => {
  store.commit("setSelectedDateFormat", selectedDateFormat.value.value);
  store.commit("setSelectedTimeFormat", selectedTimeFormat.value.value);
  toaster.success(t("common.label.success"), {
    description: t("common.message.dateTimeFormatUpdated"),
  });
};

defineExpose({
  save: saveFormats,
  canSave,
  selectedDateFormat,
  selectedTimeFormat,
});

onMounted(() => {
  dateFormats.value = getSelectedDateFormatOptions(t);
  timeFormats.value = getSelectedTimeFormatOptions(t);

  const currentDateFormat = getSelectedDateFormat.value || DEFAULT_DATE_FORMAT;
  const currentTimeFormat = getSelectedTimeFormat.value || DEFAULT_TIME_FORMAT;

  selectedDateFormat.value =
    dateFormats.value.find((format) => format.value === currentDateFormat) || dateFormats.value[0];
  selectedTimeFormat.value =
    timeFormats.value.find((format) => format.value === currentTimeFormat) || timeFormats.value[0];
});
</script>

<template>
  <div class="flex gap-4">
    <div class="flex flex-col gap-2 flex-1">
      <label class="text-sm font-medium">{{ $t("common.label.dateFormat") }}</label>
      <SnsSelect v-model="selectedDateFormat" :items="dateFormats" />
      <span v-if="showExamples" class="text-xs text-base-content/60">{{ selectedDateFormat.example }}</span>
    </div>

    <div class="flex flex-col gap-2 flex-1">
      <label class="text-sm font-medium">{{ $t("common.label.timeFormat") }}</label>
      <SnsSelect v-model="selectedTimeFormat" :items="timeFormats" />
      <span v-if="showExamples" class="text-xs text-base-content/60">{{ selectedTimeFormat.example }}</span>
    </div>
  </div>
</template>
